import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CmpsDialog } from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import { GoalThreshold } from '../../coaching-base/config';
import {
    CoachingDiffDecorateConnector,
    CoachingDiffDecoratorService,
    DiffItem,
} from '../../coaching-base/diff-decorator';
import {
    CommitmentApprovalDto,
    CommitmentKpi,
    EntityType,
    GoalStatus,
} from '../../coaching-base/general';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { TranslationKeyMap } from '../coaching-commitment.i18n';
import { GoalFormValue } from '../commitment-goals/goals.commitment.form';
import { FocusKPI } from '../models/commitment.model';

import { DisputedGoalDialogComponent } from './disputed-goal-dialog.component';
import { GoalCategoryItemView } from './goal-category-item-view.model';
import { GoalCategoryType } from './goal-category-type.model';
import { reload, setCategoryOpenState, sort } from './goal-list.action';
import { coachingCommitmentGoalListReducer } from './goal-list.reducer';
import { GoalListState } from './goal-list.state';

// TODO rename to 'goal-list'
@Component({
    selector: 'cmps-recent-goals-commitment',
    templateUrl: './recent.goal.commitment.component.html',
    host: {
        class: 'cmps-recent-goals-commitment',
    },
})
@TakeUntilDestroy()
@UseStore<GoalListState>(
    'coachingCommitment.goalList',
    coachingCommitmentGoalListReducer,
)
export class RecentGoalsCommitmentComponent
    implements
        OnChanges,
        OnInit,
        OnDestroy,
        StateToProps<GoalListState>,
        CoachingDiffDecorateConnector
{
    state: GoalListState;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _GoalStatus = GoalStatus;
    /** @private */ _GoalCategoryType = GoalCategoryType;

    @Input() goals: GoalFormValue[];
    @Input() group: FormGroup;
    @Input() commitmentKpis: CommitmentKpi[];
    @Input() focusKpis: FocusKPI[];
    @Input() goalsFromOtherCommitments: CommitmentKpi[];
    @Input() selectedGoalTrackingId: number;
    @Input() isDisabled: boolean;

    /** these are also used in pillar 2 */
    @Input() goalThresholds: GoalThreshold[];

    /** commitment approval, if any.
     * CoachingDiffDecorateConnector is not extended/used because it is purely for presentation purposes */
    @Input() approval: CommitmentApprovalDto;

    /** config value of commitment for new focus selector */
    @Input() newFocusSelectorWithYield: boolean;

    @Output() sortChange: EventEmitter<'asc' | 'desc'>;
    @Output() reviewGoal: EventEmitter<CommitmentKpi>;
    @Output() updateGoal: EventEmitter<CommitmentKpi>;
    @Output() reviewReadOnlyGoal: EventEmitter<CommitmentKpi>;
    @Output() createGoal: EventEmitter<void>;
    @Output() manageGoal: EventEmitter<boolean>;

    /** @deprecated */
    @Output() isOriginalWithDraftTrackingIdMap: EventEmitter<{
        [trackingId: number]: boolean;
    }>;

    /** @private */
    _updateParentGoalMetricData: (value: GoalFormValue) => void;

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _ngZone: NgZone,
        private readonly _translateService: TranslateService,
        private readonly _diffDecoratorService: CoachingDiffDecoratorService,
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
    ) {
        this.sortChange = new EventEmitter();
        this.updateGoal = new EventEmitter();
        this.reviewGoal = new EventEmitter();
        this.reviewReadOnlyGoal = new EventEmitter();
        this.createGoal = new EventEmitter();
        this.manageGoal = new EventEmitter();
        this.isOriginalWithDraftTrackingIdMap = new EventEmitter();

        this._updateParentGoalMetricData = (value: GoalFormValue): void => {
            const goal = this.state.categories?.[0]?.items?.find(
                (e) => e.goal?.tracking_id === value.tracking_id,
            )?.goal;
            if (goal == null) {
                return;
            }

            /* bypass state reducer */
            goal.actual = value.actual;
            goal.target = value.target;
            goal.percentToTarget = value.percentToTarget;
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.state == null) {
            return;
        }

        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('focusKpis') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('commitmentKpis') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('goalsFromOtherCommitments')
        ) {
            this._reload(false);
        }

        // eslint-disable-next-line no-prototype-builtins
        if (
            this.state.categories != null &&
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('selectedGoalTrackingId')
        ) {
            const category = this.state.categories.find((e) => {
                return e.items.some(
                    (ee) => ee.goal.tracking_id === this.selectedGoalTrackingId,
                );
            });
            if (category != null && category.isOpen !== true) {
                useDispatcher(
                    this,
                    setCategoryOpenState({
                        index: this.state.categories.indexOf(category),
                        isOpen: true,
                    }),
                );
            }
        }
    }

    ngOnInit(): void {
        this._diffDecoratorService.registerForEntityType(EntityType.Goal, this);

        this._reload(true);
    }

    ngOnDestroy(): void {
        this._diffDecoratorService.unRegisterForEntityType(
            EntityType.Goal,
            this,
        );

        this.sortChange.complete();
        this.updateGoal.complete();
        this.reviewGoal.complete();
        this.reviewReadOnlyGoal.complete();
        this.createGoal.complete();
        this.manageGoal.complete();
        this.isOriginalWithDraftTrackingIdMap.complete();
    }

    stateToProps(prevState?: GoalListState): void {
        if (prevState.categories !== this.state.categories) {
            this._ngZone.onStable.pipe(take(1)).subscribe({
                complete: () => this._diffDecoratorService.redecorate(this),
            });
        }

        if (
            prevState.isOriginalWithDraftTrackingIdMap !==
            this.state.isOriginalWithDraftTrackingIdMap
        ) {
            this.isOriginalWithDraftTrackingIdMap.emit(
                this.state.isOriginalWithDraftTrackingIdMap,
            );
        }
    }

    ddcDecorate(
        diffItem: DiffItem,
        state: 'change' | 'accept' | 'dispute',
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const el = this._el.nativeElement.querySelector(
            `[data-diffitemid="${diffItem.diffItemId}"]`,
        ) as HTMLElement;
        if (!el) {
            return;
        }
        // for each class 'cause ie 11
        el.classList.add(`cmps-recent-goals-commitment--dd-goal-${state}`);
        el.classList.add(`cmps-recent-goals-commitment--dd-goal-bg-${state}`);
    }

    ddcUnDecorate(
        diffItem: DiffItem,
        state: 'change' | 'accept' | 'dispute',
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const el = this._el.nativeElement.querySelector(
            `[data-diffitemid="${diffItem.diffItemId}"]`,
        ) as HTMLElement;
        if (!el) {
            return;
        }
        // for each class 'cause ie 11
        el.classList.remove(`cmps-recent-goals-commitment--dd-goal-${state}`);
        el.classList.remove(
            `cmps-recent-goals-commitment--dd-goal-bg-${state}`,
        );
    }

    /** @private */
    _onSortClick(): void {
        useDispatcher(this, sort({}));

        // TODO refactor. parent should not be responsible for sorting
        this.sortChange.emit(this.state.sortOrder);
    }

    /** @private */
    _onCategoryIsOpenChange($event: boolean, index: number): void {
        if (this.state.categories[index].isOpen !== $event) {
            useDispatcher(
                this,
                setCategoryOpenState({ index, isOpen: $event }),
            );
        }
    }

    /** @private */
    _onUpdateGoalClick(evt: MouseEvent, item: GoalCategoryItemView): void {
        evt.preventDefault();
        evt.stopPropagation();
        this.updateGoal.emit(item.goal);

        this._checkReadWrite(item);
    }

    /** @private */
    _onReviewGoalClick(evt: MouseEvent, item: GoalCategoryItemView): void {
        evt.preventDefault();
        evt.stopPropagation();

        // for isOriginalWithDraft: always open original items that have an existing draft update as read only
        // TODO revise. for isOriginalWithDraft: 'close goal' button is still clickable on the form itself. maybe it should be disabled as well
        // TODO parent component isn't aware of draft update goals, so buttons are shown when it's not triggered from here and parent preselects it
        if (
            item.isFromOtherCommitment ||
            item.isOriginalWithDraft ||
            item.goal.statusId === GoalStatus.CLOSED
        ) {
            this.reviewReadOnlyGoal.emit(item.goal);
            return;
        }

        // always open disputed items in edit mode
        if (this._checkReadWrite(item)) {
            this.updateGoal.emit(item.goal);
            return;
        }

        this.reviewGoal.emit(item.goal);
    }

    /** @private */
    _onCreateGoalClick($event: MouseEvent): void {
        $event.stopPropagation();
        this.newFocusSelectorWithYield
            ? this.manageGoal.emit()
            : this.createGoal.emit();
    }

    private _reload(reset: boolean): void {
        useDispatcher(
            this,
            reload({
                commitmentKpis: this.commitmentKpis,
                goalsFromOtherCommitments: this.goalsFromOtherCommitments,
                focusKpis: this.focusKpis,
                goalThresholds: this.goalThresholds,
                approval: this.approval,
                authUserId: this._authService.user.id,
                reset,
            }),
        );
    }

    // this is not called in ngOnChanges with selectedGoalTrackingId, in order to avoid initial triggering
    private _checkReadWrite(item: GoalCategoryItemView): boolean {
        if (!item.disputed) {
            return false;
        }
        const ref = this._dialog.open(DisputedGoalDialogComponent, {
            disableClose: true,
        });
        ref.result.pipe(takeUntilDestroy(this)).subscribe();
        return true;
    }
}
